import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CalendarDays, Loader2, Clock, X, Plug, Settings2, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  Requested: "text-amber-600", Confirmed: "text-emerald-600", Rescheduled: "text-sky-600",
  Completed: "text-slate-500", Cancelled: "text-red-600", "No Show": "text-red-500",
};
const APPT_STATUSES = ["Requested", "Confirmed", "Rescheduled", "Completed", "Cancelled", "No Show"];

function fmt(dtIso, tz) {
  if (!dtIso) return "Date not set";
  const d = new Date(dtIso);
  if (Number.isNaN(d.getTime())) return "Date not set";
  try { return d.toLocaleString("en-US", { timeZone: tz || "America/New_York", dateStyle: "medium", timeStyle: "short" }); }
  catch { return d.toLocaleString(); }
}

function CreateDialog({ onClose, onCreated, agents }) {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [f, setF] = useState({ lead_id: "", agent_id: "", type: "Consultation", start: "", duration_minutes: 30, timezone: "America/New_York", method: "phone", location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/leads", { params: { limit: 200 } }).then(({ data }) => setLeads(Array.isArray(data) ? data : (data.items || []))).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.lead_id || !f.agent_id || !f.start) { toast.error("Lead, agent and start time are required"); return; }
    setBusy(true);
    try {
      const iso = new Date(f.start).toISOString();
      const { data } = await api.post("/appointments", { ...f, start: iso, duration_minutes: Number(f.duration_minutes) });
      toast.success(t("appt.created")); onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4" data-testid="appt-create-dialog">
      <div className="w-full max-w-lg space-y-3 rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between"><div className="font-semibold">{t("appt.new")}</div>
          <button onClick={onClose} data-testid="appt-create-close"><X className="h-4 w-4" /></button></div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label className="text-xs">{t("appt.lead")}</Label>
            <Select value={f.lead_id} onValueChange={(v) => set("lead_id", v)}>
              <SelectTrigger data-testid="appt-lead"><SelectValue placeholder="Select lead" /></SelectTrigger>
              <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.client_name || l.display_id}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{t("appt.agent")}</Label>
            <Select value={f.agent_id} onValueChange={(v) => set("agent_id", v)}>
              <SelectTrigger data-testid="appt-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
              <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{t("appt.type")}</Label><Input value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="appt-type" /></div>
          <div><Label className="text-xs">{t("appt.start")}</Label><Input type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="appt-start" /></div>
          <div><Label className="text-xs">{t("appt.duration")}</Label><Input type="number" value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="appt-duration" /></div>
          <div><Label className="text-xs">{t("appt.method")}</Label>
            <Select value={f.method} onValueChange={(v) => set("method", v)}>
              <SelectTrigger data-testid="appt-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="phone">Phone</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="in_person">In person</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-xs">{t("appt.location")}</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="appt-location" /></div>
          <div className="col-span-2"><Label className="text-xs">{t("appt.notes")}</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="appt-notes" /></div>
        </div>
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="appt-create-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("appt.create")}</Button></div>
      </div>
    </div>
  );
}

function DetailDrawer({ appt, onClose, onChange }) {
  const { t } = useTranslation();
  const [reschedule, setReschedule] = useState("");
  const act = async (path, body) => {
    try { const { data } = await api.post(`/appointments/${appt.id}/${path}`, body || {}); toast.success(t("common.saved")); onChange(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/50" data-testid="appt-drawer" onClick={onClose}>
      <div className="h-full w-full max-w-md space-y-4 overflow-y-auto border-l bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><div className="font-semibold">{appt.type} · {appt.display_id}</div>
          <button onClick={onClose} data-testid="appt-drawer-close"><X className="h-4 w-4" /></button></div>
        <div className="space-y-1 text-sm">
          <div><span className="text-muted-foreground">{t("appt.status")}: </span><span className={STATUS_COLORS[appt.status]} data-testid="appt-status">{appt.status}</span></div>
          <div><span className="text-muted-foreground">{t("appt.when")}: </span>{fmt(appt.start, appt.timezone)} ({appt.timezone})</div>
          <div><span className="text-muted-foreground">{t("appt.agent")}: </span>{appt.agent_name}</div>
          <div><span className="text-muted-foreground">{t("appt.method")}: </span>{appt.method} {appt.location ? `· ${appt.location}` : ""}</div>
          <div><span className="text-muted-foreground">Confirmation: </span>{appt.confirmation_status}</div>
          <div><span className="text-muted-foreground">Reminder: </span>{appt.reminder_status}</div>
          <div><span className="text-muted-foreground">Google sync: </span><span data-testid="appt-google-sync">{appt.google_sync_status}</span></div>
          {appt.notes ? <div className="text-muted-foreground">{appt.notes}</div> : null}
        </div>
        <div className="space-y-2 border-t pt-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => act("complete")} data-testid="appt-complete">{t("appt.complete")}</Button>
            <Button size="sm" variant="outline" onClick={() => act("no-show")} data-testid="appt-noshow">{t("appt.noShow")}</Button>
            <Button size="sm" variant="outline" onClick={() => act("cancel", { reason: "Cancelled from console" })} data-testid="appt-cancel">{t("appt.cancel")}</Button>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label className="text-xs">{t("appt.reschedule")}</Label>
              <Input type="datetime-local" value={reschedule} onChange={(e) => setReschedule(e.target.value)} data-testid="appt-reschedule-input" /></div>
            <Button size="sm" onClick={() => reschedule && act("reschedule", { start: new Date(reschedule).toISOString(), timezone: appt.timezone })} data-testid="appt-reschedule-submit">{t("appt.reschedule")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProvidersPanel() {
  const { t } = useTranslation();
  const [sms, setSms] = useState(null);
  const [gc, setGc] = useState(null);
  const load = () => { api.get("/sms/provider-status").then(({ data }) => setSms(data)).catch(() => {}); api.get("/google-calendar/status").then(({ data }) => setGc(data)).catch(() => {}); };
  useEffect(load, []);
  const connectGoogle = async () => {
    try { const { data } = await api.get("/google-calendar/connect"); if (data.authorization_url) window.location.assign(data.authorization_url); else toast.error("Google Calendar not configured"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const disconnectGoogle = async () => { await api.delete("/google-calendar/disconnect"); load(); };
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="providers-panel">
      <div className="rounded-lg border bg-card p-4 text-sm" data-testid="twilio-status">
        <div className="mb-2 flex items-center gap-2 font-semibold"><Plug className="h-4 w-4" /> {t("providers.twilio")}</div>
        {!sms ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <div className="space-y-1">
            <StatusRow ok={sms.configured} label={sms.configured ? t("providers.configured") : t("providers.notConfigured")} />
            {!sms.configured && sms.missing?.length ? <div className="text-xs text-amber-600">{t("providers.missing")}: {sms.missing.join(", ")}</div> : null}
            <Row k={t("providers.messagingService")} v={sms.messaging_service_configured ? "yes" : "no"} />
            <Row k="A2P" v={sms.a2p_status} />
            <Row k={t("providers.sendingNumber")} v={sms.sending_number || "—"} />
            <Row k={t("providers.inboundWebhook")} v={sms.inbound_webhook_configured ? "configured" : "default"} />
            <Row k={t("providers.deliveryCallback")} v={sms.delivery_callback_configured ? "configured" : "default"} />
            <Row k={t("providers.lastEvent")} v={sms.last_successful_event ? new Date(sms.last_successful_event).toLocaleString() : "—"} />
            <Row k={t("providers.lastError")} v={sms.most_recent_error?.code || "—"} />
          </div>
        )}
      </div>
      <div className="rounded-lg border bg-card p-4 text-sm" data-testid="google-status">
        <div className="mb-2 flex items-center gap-2 font-semibold"><CalendarDays className="h-4 w-4" /> {t("providers.google")}</div>
        {!gc ? <Loader2 className="h-4 w-4 animate-spin" /> : !gc.configured ? (
          <div className="space-y-2"><StatusRow ok={false} label={t("providers.notConfigured")} />
            {gc.missing?.length ? <div className="text-xs text-amber-600">{t("providers.missing")}: {gc.missing.join(", ")}</div> : null}
            <p className="text-xs text-muted-foreground">{t("providers.googleHint")}</p></div>
        ) : (
          <div className="space-y-2">
            <StatusRow ok={gc.status === "connected"} label={gc.status} />
            {gc.status === "connected"
              ? <Button size="sm" variant="outline" onClick={disconnectGoogle} data-testid="google-disconnect">{t("providers.disconnect")}</Button>
              : <Button size="sm" onClick={connectGoogle} data-testid="google-connect">{t("providers.connect")}</Button>}
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular-nums">{v}</span></div>;
const StatusRow = ({ ok, label }) => <div className="flex items-center gap-2">{ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}<span>{label}</span></div>;

function AvailabilityPanel() {
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/agency/availability").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <Loader2 className="h-4 w-4 animate-spin" />;
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));
  const save = async () => { setBusy(true); try { const { data } = await api.put("/agency/availability", a); setA(data); toast.success(t("common.saved")); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <div className="max-w-xl space-y-3 rounded-lg border bg-card p-4 text-sm" data-testid="availability-panel">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">{t("avail.timezone")}</Label><Input value={a.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="avail-timezone" /></div>
        <div><Label className="text-xs">{t("avail.duration")}</Label><Input type="number" value={a.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid="avail-duration" /></div>
        <div><Label className="text-xs">{t("avail.hoursStart")}</Label><Input value={a.business_hours?.start} onChange={(e) => set("business_hours", { ...a.business_hours, start: e.target.value })} data-testid="avail-start" /></div>
        <div><Label className="text-xs">{t("avail.hoursEnd")}</Label><Input value={a.business_hours?.end} onChange={(e) => set("business_hours", { ...a.business_hours, end: e.target.value })} data-testid="avail-end" /></div>
        <div><Label className="text-xs">{t("avail.minNotice")}</Label><Input type="number" value={a.min_notice_minutes} onChange={(e) => set("min_notice_minutes", Number(e.target.value))} data-testid="avail-notice" /></div>
        <div><Label className="text-xs">{t("avail.maxAdvance")}</Label><Input type="number" value={a.max_advance_days} onChange={(e) => set("max_advance_days", Number(e.target.value))} data-testid="avail-advance" /></div>
        <div><Label className="text-xs">{t("avail.bufferBefore")}</Label><Input type="number" value={a.buffer_before} onChange={(e) => set("buffer_before", Number(e.target.value))} data-testid="avail-buffer-before" /></div>
        <div><Label className="text-xs">{t("avail.bufferAfter")}</Label><Input type="number" value={a.buffer_after} onChange={(e) => set("buffer_after", Number(e.target.value))} data-testid="avail-buffer-after" /></div>
      </div>
      <div><Label className="text-xs">{t("avail.businessDays")}</Label>
        <div className="mt-1 flex gap-1">
          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
            <button key={d} data-testid={`avail-day-${d}`}
              onClick={() => set("business_days", a.business_days.includes(d) ? a.business_days.filter((x) => x !== d) : [...a.business_days, d].sort())}
              className={`h-8 w-8 rounded text-xs ${a.business_days.includes(d) ? "bg-[#B28A3D] text-[#17324D]" : "bg-muted text-muted-foreground"}`}>
              {["M", "T", "W", "T", "F", "S", "S"][d - 1]}</button>
          ))}
        </div></div>
      <Button size="sm" onClick={save} disabled={busy} data-testid="avail-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
    </div>
  );
}

export default function Appointments() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = ["admin", "ops_manager", "platform_owner"].includes(user?.role);
  const [tab, setTab] = useState("list");
  const [appts, setAppts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({ status: "", agent_id: "" });
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {}; if (filters.status) params.status = filters.status; if (filters.agent_id) params.agent_id = filters.agent_id;
    api.get("/appointments", { params }).then(({ data }) => setAppts(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filters]);
  useEffect(() => { if (isAdmin) api.get("/users").then(({ data }) => setAgents(data)).catch(() => setAgents([])); else if (user) setAgents([{ id: user.id, name: user.name }]); }, [isAdmin, user]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("google") === "connected") toast.success("Google Calendar connected");
    if (p.get("google") === "error") toast.error("Google Calendar connection failed");
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    appts.forEach((a) => {
      const raw = a.start || a.start_time;
      const parsed = new Date(raw);
      const d = !raw || Number.isNaN(parsed.getTime()) ? "Date not set" : parsed.toLocaleDateString("en-US", { timeZone: a.timezone || "America/New_York", dateStyle: "full" });
      (g[d] || (g[d] = [])).push({ ...a, start: raw, type: a.type || a.appointment_type || "Consultation", agent_name: a.agent_name || a.agent || "Unassigned" });
    });
    return g;
  }, [appts]);

  const TABS = [["list", t("appt.tabList"), CalendarDays], ["availability", t("appt.tabAvailability"), Settings2], ["providers", t("appt.tabProviders"), Plug]];

  return (
    <div className="space-y-5" data-testid="appointments-page">
      <PageHeader title={t("appt.title")} subtitle={t("appt.subtitle")} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-b">
        <div className="flex flex-wrap gap-2">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`appt-tab-${k}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${tab === k ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>
              <Icon className="h-4 w-4" /> {label}</button>
          ))}
        </div>
        {tab === "list" && <Button size="sm" onClick={() => setCreating(true)} data-testid="appt-new-btn">{t("appt.new")}</Button>}
      </div>

      {tab === "list" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-40" data-testid="appt-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("appt.allStatuses")}</SelectItem>{APPT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {isAdmin && (
              <Select value={filters.agent_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, agent_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-48" data-testid="appt-filter-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent><SelectItem value="all">{t("appt.allAgents")}</SelectItem>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : appts.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-card/50 p-10 text-center text-sm text-muted-foreground" data-testid="appt-empty">{t("appt.empty")}</div>
          ) : (
            Object.entries(grouped).map(([day, list]) => (
              <div key={day}>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">{day}</div>
                <div className="space-y-1.5">
                  {list.map((a) => (
                    <button key={a.id} onClick={() => setSelected(a)} data-testid={`appt-row-${a.id}`}
                      className="flex w-full items-center justify-between rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-accent">
                      <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="tabular-nums">{fmt(a.start, a.timezone)}</span>
                        <span>{a.type}</span><span className="text-muted-foreground">· {a.agent_name}</span></div>
                      <span className={STATUS_COLORS[a.status]}>{a.status}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "availability" && <AvailabilityPanel />}
      {tab === "providers" && <ProvidersPanel />}

      {creating && <CreateDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} agents={agents} />}
      {selected && <DetailDrawer appt={selected} onClose={() => setSelected(null)} onChange={(d) => { setSelected(d); load(); }} />}
    </div>
  );
}
